if (!customElements.get('product-checkbox-validator')) {
  class ProductCheckboxValidator extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      if (!this.checkbox) {
        return;
      }

      // Find the product form
      const formId = this.checkbox.getAttribute('form');
      this.form = document.getElementById(formId);
      if (!this.form) {
        return;
      }

      this.addToCartButton = this.form.querySelector('button[name="add"]')
        || document.querySelector('[data-enquire-now-button]');
      if (!this.addToCartButton) {
        return;
      }

      // Check if there's a custom-product-attributes component on the page
      this.customAttributesComponent = document.querySelector('custom-product-attributes');

      // Store the original button text
      this.originalButtonText = this.addToCartButton.dataset.addToCartText
        || this.addToCartButton.dataset.enquireText
        || this.addToCartButton.textContent;

      // Check if button text indicates product is actually unavailable
      const buttonText = this.addToCartButton.textContent.trim();
      this.originalDisabledState = this.isButtonTextUnavailable(buttonText);

      // Add event listener to checkbox
      this.checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));

      // Listen for variant changes that might affect the button state
      const productSection = this.closest('.product');
      if (productSection) {
        productSection.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Watch for other scripts changing the button (variant picker, custom attributes, etc)
      this.observeButtonChanges();

      // Initialize button state
      this.updateButtonState();
    }

    handleCheckboxChange() {
      this.updateButtonState();
    }

    handleVariantChange(event) {
      // Update the original button text when variant changes
      if (this.addToCartButton.dataset.addToCartText) {
        this.originalButtonText = this.addToCartButton.dataset.addToCartText;
      }

      // Update the original disabled state based on button text
      const variant = event.detail?.variant;
      if (variant) {
        this.originalDisabledState = !variant.available;
      } else {
        const buttonText = this.addToCartButton.textContent.trim();
        this.originalDisabledState = this.isButtonTextUnavailable(buttonText);
      }

      // Re-evaluate button state
      this.updateButtonState();
    }

    isButtonTextUnavailable(buttonText) {
      // Only check for TRUE unavailability (sold out, no stock)
      // NOT selection requirements like "Please select all options"
      const unavailableStrings = [
        'Sold out',
        'Unavailable',
        'Out of stock'
      ];

      return unavailableStrings.some(str =>
        buttonText.toLowerCase().includes(str.toLowerCase())
      );
    }

    observeButtonChanges() {
      // Use MutationObserver to watch for other scripts changing the button
      this.buttonObserver = new MutationObserver(() => {
        // Re-apply our validation logic whenever button changes
        this.updateButtonState();
      });

      this.buttonObserver.observe(this.addToCartButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    updateButtonState() {
      // Prevent infinite loop - don't update if we're already updating
      if (this.isUpdating) return;
      this.isUpdating = true;

      const isChecked = this.checkbox.checked;

      // Check if button text indicates variant is unavailable (sold out, etc)
      const buttonText = this.addToCartButton.textContent.trim();
      const isVariantUnavailable = this.isButtonTextUnavailable(buttonText);

      if (!isVariantUnavailable && !this.isButtonTextUnavailable(this.originalButtonText)) {
        this.originalDisabledState = false;
      }

      // Don't override if variant is truly unavailable (sold out)
      if (isVariantUnavailable) {
        this.isUpdating = false;
        return;
      }

      // Don't override if variant options haven't been fully selected yet
      if (this.addToCartButton.dataset.optionsIncomplete === 'true') {
        this.isUpdating = false;
        return;
      }

      // Check if custom attributes are all selected (if component exists)
      let customAttributesValid = true;
      if (this.customAttributesComponent && typeof this.customAttributesComponent.checkAllSelectionsComplete === 'function') {
        customAttributesValid = this.customAttributesComponent.checkAllSelectionsComplete();
      }

      // Priority order:
      // 1. Custom attributes must be selected first
      // 2. Then checkbox must be checked

      if (!customAttributesValid) {
        // Custom attributes not selected - let that component handle the message
        // Don't override the button state
        this.isUpdating = false;
        return;
      }

      if (!isChecked) {
        // Custom attributes are valid, but checkbox not checked
        this.addToCartButton.setAttribute('aria-disabled', 'true');
        this.addToCartButton.disabled = true;
        this.addToCartButton.textContent = this.dataset.requiredMessage || 'Please confirm acknowledgement';
      } else {
        // Both valid - enable the button
        this.addToCartButton.removeAttribute('aria-disabled');
        this.addToCartButton.disabled = false;
        // Iron sets keep the loft list's "Add 8 clubs to cart" label.
        this.addToCartButton.textContent = this.customAttributesComponent?.loftGrid
          ? this.customAttributesComponent.loftButtonText()
          : this.originalButtonText;
      }

      // Allow updates again after a short delay
      setTimeout(() => {
        this.isUpdating = false;
      }, 100);
    }

    disconnectedCallback() {
      if (this.checkbox) {
        this.checkbox.removeEventListener('change', this.handleCheckboxChange.bind(this));
      }

      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
      }

      const productSection = this.closest('.product');
      if (productSection) {
        productSection.removeEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }
    }
  }

  customElements.define('product-checkbox-validator', ProductCheckboxValidator);
}

